/**
 * Zod schema for the `Artefact` persistence shape + its slug-history row.
 *
 * Mirrors `../types/artefact.ts`. Spec authority: `architecture.md §4.7`
 * `CREATE TABLE artefacts` + `CREATE TABLE artefact_slug_history`.
 */

import { z } from "zod";
import type { Artefact, ArtefactSlugHistoryEntry, ArtefactStatus } from "../types/artefact.js";
import { UUID_RE, HANDLE_SLUG_RE } from "../urls/types.js";

const uuid = z.string().regex(UUID_RE);
const slug = z.string().regex(HANDLE_SLUG_RE);
const timestamp = z.number().int().nonnegative();

export const ArtefactStatusSchema: z.ZodType<ArtefactStatus> = z.enum([
  "active",
  "dormant",
  "retired",
]);

export const ArtefactSchema = z
  .object({
    uuid,
    slug,
    name: z.string(),
    kindUuid: uuid,
    parentArtefactUuid: uuid.nullable().optional(),
    originAttemptUuid: uuid.nullable().optional(),
    status: ArtefactStatusSchema,
    createdAt: timestamp,
    updatedAt: timestamp,
  })
  .strict();

export const ArtefactSlugHistoryEntrySchema = z
  .object({
    artefactUuid: uuid,
    slug,
    retiredAt: timestamp,
  })
  .strict();

type _AssertArtefact =
  z.infer<typeof ArtefactSchema> extends Artefact
    ? Artefact extends z.infer<typeof ArtefactSchema>
      ? true
      : never
    : never;
type _AssertArtefactSlugHistory =
  z.infer<typeof ArtefactSlugHistoryEntrySchema> extends ArtefactSlugHistoryEntry
    ? ArtefactSlugHistoryEntry extends z.infer<typeof ArtefactSlugHistoryEntrySchema>
      ? true
      : never
    : never;
export type __ArtefactSchemaAsserts = [_AssertArtefact, _AssertArtefactSlugHistory];
